import { createReducer } from '@reduxjs/toolkit'

import { TDashboardGroupResponse } from 'types/GlobalDashboardGroup'
import { insertItems, removeItem, upsertItem } from 'redux/utils'

import * as actions from '../../actions/DashboardGroups/actions'
import * as dashboardActions from '../../actions/Dashboards/actions'
import * as authenticationActions from '../../actions/Authentication/actions'

export interface DashboardGroupReducerType {
  data: {
    [id: string]: TDashboardGroupResponse
  }
  deleted: {
    [id: string]: TDashboardGroupResponse
  }
  fetching: boolean
  fetched: boolean
  deletedFetching: boolean
  deletedFetched: boolean
}

// Initial state
const INITIAL_STATE: DashboardGroupReducerType = {
  data: {},
  deleted: {},
  fetching: false,
  fetched: false,
  deletedFetching: false,
  deletedFetched: false
}

// Reducer
const dashboardGroupReducer = createReducer(INITIAL_STATE, (builder) => {
  builder

    // tryGetAllDashboardGroups
    .addCase(actions.getAll, (state) => {
      state.fetching = true
    })
    .addCase(actions.getAllSuccess, (state, action) => {
      state.data = insertItems(action.payload)
      state.fetching = false
      state.fetched = true
    })
    .addCase(actions.getAllFailed, (state) => {
      state.fetching = false
    })

    // tryUpdateDashboardGroup
    .addCase(actions.updateSuccess, (state, action) => {
      state.data = upsertItem(state.data, action.payload)
    })

    // tryDeleteDashboardGroup
    .addCase(actions.destroySuccess, (state, action) => {
      state.data = removeItem(state.data, action.payload.id)
    })

    // tryGetDeletedDashboardGroups
    .addCase(actions.getDeleted, (state) => {
      state.deletedFetching = true
    })
    .addCase(actions.getDeletedSuccess, (state, action) => {
      state.deleted = insertItems(action.payload)
      state.deletedFetching = false
      state.deletedFetched = true
    })
    .addCase(actions.getDeletedFailed, (state) => {
      state.deletedFetching = false
    })

    // tryRestoreDashboardGroup
    .addCase(actions.restoreSuccess, (state, action) => {
      state.deleted = removeItem(state.deleted, action.payload.id)
      state.data = upsertItem(state.data, action.payload)
    })

    // tryCopyDashboardGroup
    .addCase(actions.copySuccess, (state, action) => {
      state.data = upsertItem(state.data, action.payload)
    })

    // tryInviteToDashboard
    .addCase(actions.inviteSuccess, (state, action) => {
      const { payload } = action

      state.data[payload.id].share_settings = payload.share_settings
    })

    // tryRevokeFromDashboard
    .addCase(actions.revokeAccessSuccess, (state, action) => {
      const { payload } = action

      state.data[payload.id].share_settings = payload.share_settings
    })

    // tryPutDashboardGroupFilterOptions
    .addCase(actions.putDashboardFiltersSuccess, (state, action) => {
      const { payload } = action

      state.data[payload.id].dashboard_filters = payload.data
    })

    // tryCreateDashboard
    .addCase(dashboardActions.createSuccess, (state, action) => {
      const { payload } = action

      state.data[payload.group.id] = payload.group
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default dashboardGroupReducer
